import startCase from 'lodash/startCase';
import moment from 'moment-timezone';
import {ActionType} from 'redux-promise-middleware';
import {getDocument} from 'ssr-window';
import StorageUtils from '@spothero/utils/storage';
import SearchUtils from 'search/search-utils';
import GoogleRemarketingUtils from 'utils/google-remarketing';
import GTMUtils from 'utils/gtm';
import SearchTracking from 'utils/search-tracking';
import SegmentUtils from 'utils/segment';
import SpotUtils from 'utils/spot';
import UserUtils from 'user/user-utils';
import {USER_LOGIN, USER_LOGOUT, USER_UPDATE} from 'user/user-actions';
import {CHECKOUT_FETCH_RATES} from 'checkout/checkout-actions';
import {SEARCH_PAN_MAP, SEARCH_SET_ZOOM} from 'search/search-actions';
import {SEARCH_REQUEST_SET_TERM} from 'search-request/search-request-actions';
import {
    SPOTS_RADIAL_INCREMENT,
    SPOTS_TILED_INCREMENT,
    SPOTS_GET_MOBILE,
    SPOTS_GET_MOBILE_PAGINATION,
    SPOTS_GET_RADIAL,
    SPOTS_GET_AIRPORT,
    SPOTS_GET_TRAVEL_DISTANCES,
} from 'spots/spots-actions';
import {FILTERS_RESET, FILTERS_UPDATE} from 'filters/filters-actions';
import trackMapAdjusted from 'segment/events/map-adjusted';
import trackProductListFiltered from 'segment/events/product-list-filtered';
import trackProductsSearched from 'segment/events/products-searched';
import trackSearchResultsList from 'segment/events/search-results-list';

const document = getDocument();
const ANALYTICS_SEARCH_UUID_COOKIE = 'sh-analytics-search-uuid';
const TTFR_SESSION = 'sh-ttfr-tracked';

export const analyticsMiddleware = store => next => action => {
    const {type, payload, meta} = action;

    switch (type) {
        case `${USER_LOGIN}_${ActionType.Fulfilled}`:
        case USER_UPDATE: {
            const storedUser = store.getState().user.data;
            const newUser = payload.id && storedUser.id !== payload.id;

            if (newUser) {
                UserUtils.identify(payload);
            }

            break;
        }

        case `${USER_LOGOUT}_${ActionType.Fulfilled}`: {
            StorageUtils.remove('sh-session-uuid', 'cookie');
            UserUtils.identify(null);

            break;
        }

        case `${SPOTS_GET_RADIAL}_${ActionType.Pending}`:
        case `${SPOTS_GET_MOBILE}_${ActionType.Pending}`: {
            if (!StorageUtils.get(TTFR_SESSION, 'session')) {
                SegmentUtils.startTimeToFirstResultTimer();
            }

            break;
        }

        case SPOTS_RADIAL_INCREMENT:
        case SPOTS_TILED_INCREMENT:
        case `${SPOTS_GET_AIRPORT}_${ActionType.Fulfilled}`:
        case `${SPOTS_GET_MOBILE}_${ActionType.Fulfilled}`:
        case `${SPOTS_GET_MOBILE_PAGINATION}_${ActionType.Fulfilled}`: {
            const {
                city: {data: city},
                destination: {data: destination},
                event: {data: event},
                search: {
                    data: {currentIncrement, destinationTitle},
                },
                searchRequest,
                filters: {activeAmenities},
            } = store.getState();

            if (
                !StorageUtils.get(TTFR_SESSION, 'session') &&
                (type === `${SPOTS_GET_MOBILE}_${ActionType.Fulfilled}` ||
                    (type === SPOTS_RADIAL_INCREMENT && currentIncrement === 1))
            ) {
                SegmentUtils.trackTimeToFirstResult();
            }

            if (
                currentIncrement === 1 ||
                (meta && meta.isMobileRequest) ||
                type === `${SPOTS_GET_AIRPORT}_${ActionType.Fulfilled}`
            ) {
                const analyticsSearchUUIDCookie = StorageUtils.get(
                    ANALYTICS_SEARCH_UUID_COOKIE,
                    'cookie'
                );
                const {searchUUID} = SearchTracking.getValues();

                if (analyticsSearchUUIDCookie !== searchUUID) {
                    trackProductsSearched({
                        city,
                        destination,
                        event,
                        destinationTitle,
                        searchRequest,
                        activeAmenities,
                        spots: payload.results,
                    });

                    StorageUtils.set(
                        ANALYTICS_SEARCH_UUID_COOKIE,
                        searchUUID,
                        'cookie'
                    );
                }
            }

            break;
        }

        case `${SPOTS_GET_TRAVEL_DISTANCES}_${ActionType.Fulfilled}`: {
            // mobile and airport track this as part of Products Searched above so no need to call it here
            if (!meta.ignoreSegment) {
                const {
                    search: {
                        data: {sortBy},
                    },
                    searchRequest: {monthly: isMonthly},
                    filters,
                } = store.getState();

                // This is a temporary event until we move to search v2 and use one API call.
                // It's a stop gap to collect data for Project Apollo that will eventually get
                // combined with the Products Searched event above when that change is made.
                trackSearchResultsList({
                    spots: action.payload[0],
                    filters,
                    sortBy,
                    isMonthly,
                });
            }

            break;
        }

        case SEARCH_REQUEST_SET_TERM: {
            const {airport, monthly} = payload;
            const termString = airport
                ? 'airport'
                : monthly
                ? 'monthly'
                : 'transient';

            GoogleRemarketingUtils.track({
                data: {
                    /* eslint-disable camelcase */
                    dynx_itemid: 's_NA',
                    dynx_itemid2: termString,
                    dynx_pagetype: 'searchresults',
                    /* eslint-enable camelcase */
                },
            });

            break;
        }

        case `${SPOTS_GET_RADIAL}_${ActionType.Fulfilled}`: {
            const {
                search: {
                    data: {searchType},
                },
            } = store.getState();

            SegmentUtils.page({
                name: `${startCase(searchType)} Search`,
            });

            break;
        }

        case `${CHECKOUT_FETCH_RATES}_${ActionType.Fulfilled}`: {
            const {
                payload: {
                    data: {data},
                },
                meta: {monthly, spotId},
            } = action;
            const {
                checkout: {previousRental},
                city: {
                    data: {timezone},
                },
                searchRequest: {starts, ends, airport, referrer, pageType},
                spot: {selected: selectedSpot},
                search: {
                    data: {referrerAffiliate},
                },
            } = store.getState();
            const isAirportSpotDetail = pageType === 'facility' && airport;
            const newRate = SpotUtils.prepare({
                spot: {
                    ...selectedSpot,
                    ...data,
                },
                searchRequest: {
                    monthly,
                },
                timezone,
            }).selectedRate;
            const newPrice = newRate ? (newRate.price / 100).toString() : 'N/A';
            const newCurrencyType = newRate
                ? newRate.currency_type
                : UserUtils.CURRENCY_TYPES.USD.toLowerCase();
            const startsMoment =
                newRate && newRate.starts
                    ? moment(newRate.starts)
                    : moment(starts);
            const endsMoment =
                newRate && newRate.ends
                    ? moment(newRate.ends)
                    : monthly
                    ? startsMoment.clone().add(1, 'month')
                    : moment(ends);
            const id = selectedSpot ? selectedSpot.spotId : spotId;
            const rentalType = airport
                ? 'airport'
                : monthly
                ? 'monthly'
                : 'transient';
            const googleRemarketingTrackingData = {
                /* eslint-disable camelcase */
                dynx_itemid: `f_${id}`,
                dynx_itemid2: rentalType,
                dynx_pagetype: 'conversionintent',
                /* eslint-enable camelcase */
            };
            const fromScreen = SearchUtils.getReferrerDetails({
                referrer,
                previousRental,
            });

            if (newRate) {
                // eslint-disable-next-line camelcase
                googleRemarketingTrackingData.dynx_totalvalue =
                    newRate.price / 100;
            }

            if (!isAirportSpotDetail) {
                GoogleRemarketingUtils.track({
                    data: googleRemarketingTrackingData,
                });

                SegmentUtils.page({
                    name: 'Checkout',
                    properties: {
                        currency: newCurrencyType,
                        price: newPrice,
                        'spot id': id,
                        'reservation duration': parseInt(
                            moment
                                .duration(endsMoment.diff(startsMoment))
                                .format('m'),
                            10
                        ),
                        'time until reservation start': parseInt(
                            moment
                                .duration(startsMoment.diff(moment()))
                                .format('m'),
                            10
                        ),
                        'from screen': fromScreen,
                        'parking type': rentalType,
                        referrer: referrer || document.referrer,
                        'referrer affiliate': referrerAffiliate,
                    },
                });
            }

            GTMUtils.push({
                event: 'priceUpdated',
                rentalPrice: newPrice,
                currencyType: newCurrencyType,
                spotId: id,
                rentalType,
            });

            break;
        }

        case FILTERS_RESET:
        case FILTERS_UPDATE: {
            const {
                city: {
                    data: {slug: citySlug},
                },
                filters,
                search: {
                    data: {sortBy},
                },
                searchRequest: {monthly: isMonthly, pageType},
                spots: {data: spots},
            } = store.getState();

            trackProductListFiltered({
                citySlug,
                pageType,
                isMonthly,
                spots,
                sortBy,
                filters: {
                    ...filters,
                    ...payload,
                    ...(type === FILTERS_RESET && {activeAmenities: []}),
                },
            });

            break;
        }

        case SEARCH_PAN_MAP:
        case SEARCH_SET_ZOOM: {
            // setting ideal distance causes zooming, use ignoreAnalytics to not fire tracking events
            const {level, previousLevel, ignoreAnalytics} = payload;
            const {
                city: {
                    data: {slug: city},
                },
                filters,
                search: {
                    data: {sortBy, searchMapInstance},
                },
                searchRequest: {monthly: isMonthly, pageType},
                spots: {data: spots},
            } = store.getState();

            if (!ignoreAnalytics) {
                const actionType =
                    type === SEARCH_PAN_MAP
                        ? 'PAN'
                        : level > previousLevel
                        ? 'ZOOM_IN'
                        : 'ZOOM_OUT';

                trackMapAdjusted({
                    actionType,
                    city,
                    filters,
                    isMonthly,
                    pageType,
                    sortBy,
                    spots,
                    mapBounds: searchMapInstance.getBounds(),
                });
            }

            break;
        }
    }

    return next(action);
};
